import React, { useState, useEffect, lazy, Suspense, useTransition } from "react";
import { createPortal } from "react-dom";
import { Routes, Route, useParams, useSearchParams } from "react-router-dom";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import { LoadingFallback } from "./components/LoadingFallback";
import { useDocset } from "@/contexts/chat/DocsetContext";
import { api } from "@/lib/api-client";
import type { Docset } from "@/types";

// Lazy load heavy components with prefetch
const AdminLogin = lazy(() => import(/* webpackPrefetch: true */ "./pages/AdminLogin"));
const AdminPanel = lazy(() => import(/* webpackPrefetch: true */ "./pages/AdminPanel"));
const Documentation = lazy(() => import(/* webpackPreload: true */ "./pages/Documentation"));

interface AppRoutesProps {
  docsets: Docset[];
  isModalOpen: boolean;
  handleModalStateChange: (isOpen: boolean) => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  docsets,
  isModalOpen,
  handleModalStateChange,
}) => (
  <Suspense fallback={<LoadingFallback />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminPanel />} />
      <Route
        path="/:docsetId"
        element={
          <DocumentationWrapper 
            docsets={docsets}
            isModalOpen={isModalOpen}
          />
        }
      />
    </Routes>
  </Suspense>
);

// Wrapper component to handle route-based docset selection AND document management
const DocumentationWrapper = ({ 
  docsets, 
  isModalOpen 
}: { 
  docsets: Docset[]
  isModalOpen: boolean
}) => {
  const { docsetId } = useParams<{ docsetId: string }>()
  const [searchParams] = useSearchParams()
  const { setSelectedDocset: setContextDocset } = useDocset()

  // State for the full list of documents for the current docset
  const [documents, setDocuments] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // State for the ID of the currently selected document
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null)
  // State for the markdown content of the selected document
  const [markdownContent, setMarkdownContent] = useState<string>('')
  // Transition state for smooth document switches
  const [isPending, startDocTransition] = useTransition()

  // Effect to fetch documents when the docsetId changes
  useEffect(() => {
    if (docsetId && docsets.some(ds => ds.id === docsetId)) {
      setContextDocset(docsetId)
      
      const fetchDocs = async () => {
        setLoading(true)
        setError(null)
        
        try {
          const docs = await api.docs.listByDocset(docsetId)
          setDocuments(docs)

          // Check if there's a source parameter in URL
          const sourceParam = searchParams.get('source')
          if (sourceParam && docs.length > 0) {
            // Find document by source parameter
            const doc = docs.find(d => d.source === sourceParam || d.id === sourceParam)
            if (doc) {
              setSelectedDocument(doc.id)
              setMarkdownContent(doc.content)
            } else {
              // If source not found, select first document
              if (docs.length > 0) {
                setSelectedDocument(docs[0].id)
                setMarkdownContent(docs[0].content)
              }
            }
          } else {
            // No source parameter, auto-select the first document
            if (docs.length > 0) {
              setSelectedDocument(docs[0].id)
              setMarkdownContent(docs[0].content)
            } else {
              // Handle case with no documents
              setSelectedDocument(null)
              setMarkdownContent('')
            }
          }
        } catch (error) {
          console.error("Failed to fetch documents:", error)
          setError(error instanceof Error ? error.message : 'Failed to fetch documents')
          setDocuments([])
        } finally {
          setLoading(false)
        }
      }

      fetchDocs()
    }
  }, [docsetId, docsets, setContextDocset, searchParams])

  // Callback for when a user clicks a document in the sidebar
  const handleDocumentSelect = (docId: string, content: string) => {
    // Use transition to keep UI responsive during heavy markdown rendering
    startDocTransition(() => {
      setSelectedDocument(docId)
      setMarkdownContent(content)
    })
  }

  const suggestions = (docsets.find(d => d.id === docsetId)?.default_questions) || []
  
  // Global loading state for full-page overlay
  const isGlobalLoading = loading || isPending

  return (
    <>
      {/* Global loading overlay - rendered at document root via portal */}
      {isGlobalLoading && createPortal(
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="text-sm text-muted-foreground">
              {loading ? 'Loading documents...' : 'Loading document...'}
            </span>
          </div>
        </div>,
        document.body
      )}
      
      <div className="flex flex-col bg-background w-screen h-screen relative">
      
      <div className="flex-shrink-0 px-4 py-2">
        <Navbar selectedDocset={docsetId || ''} />
      </div>
      <div className="flex-1 min-h-0 w-full">
        <Documentation
          documents={documents}
          loading={loading}
          error={error}
          selectedDocset={docsetId || ''}
          selectedDocument={selectedDocument}
          markdownContent={markdownContent}
          onDocumentSelect={handleDocumentSelect}
          suggestions={suggestions}
          isModalOpen={isModalOpen}
          isPending={isPending}
        />
      </div>
    </div>
    </>
  )
}

export default AppRoutes;
